import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, FileText, Package, Users, Boxes,
  BarChart3, Settings as Cog, LogOut, ArrowLeft, AlertTriangle, Banknote, UserCog, ShieldAlert, Wallet, Calculator, PiggyBank,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { api, rd } from "./posShared";
import Productos from "./Productos";
import Clientes from "./Clientes";
import Inventario from "./Inventario";
import Facturar from "./Facturar";
import Facturas from "./Facturas";
import Cotizaciones from "./Cotizaciones";
import Reportes from "./Reportes";
import CierreCaja from "./CierreCaja";
import Configuracion from "./Configuracion";
import Usuarios from "./Usuarios";
import Auditoria from "./Auditoria";
import CuentasPorCobrar from "./CuentasPorCobrar";
import CajaChica from "./CajaChica";
import InvoicePrint from "./InvoicePrint";
import OfflineSync from "./OfflineSync";
import TopNav from "../../components/TopNav";

const NAV = [
  { key: "inicio", label: "Inicio", icon: LayoutDashboard, adminOnly: true },
  { key: "facturar", label: "Facturar", icon: Receipt, cashier: true },
  { key: "facturas", label: "Facturas", icon: FileText, cashier: true },
  { key: "cotizaciones", label: "Cotizaciones", icon: Calculator, cashier: true },
  { key: "productos", label: "Productos", icon: Package, adminOnly: true },
  { key: "clientes", label: "Clientes", icon: Users, cashier: true },
  { key: "inventario", label: "Inventario", icon: Boxes, adminOnly: true },
  { key: "reportes", label: "Reportes", icon: BarChart3, adminOnly: true },
  { key: "cierre", label: "Cierre de caja", icon: Banknote, cashier: true },
  { key: "cxc", label: "Cuentas por cobrar", icon: Wallet, adminOnly: true },
  { key: "cajachica", label: "Caja chica y gastos", icon: PiggyBank, adminOnly: true },
  { key: "usuarios", label: "Usuarios", icon: UserCog, adminOnly: true },
  { key: "auditoria", label: "Auditoría", icon: ShieldAlert, adminOnly: true },
  { key: "config", label: "Configuración", icon: Cog, adminOnly: true },
];

export default function Pos() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";
  const [tab, setTab] = useState("inicio");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [movements, setMovements] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dash, setDash] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [convertQuote, setConvertQuote] = useState(null);

  const loadProducts = useCallback(() => api.get("/pos/products").then((r) => setProducts(r.data)).catch(() => {}), []);
  const loadCustomers = useCallback(() => api.get("/pos/customers").then((r) => setCustomers(r.data)).catch(() => {}), []);
  const loadInvoices = useCallback(() => api.get("/pos/invoices").then((r) => setInvoices(r.data)).catch(() => {}), []);
  const loadMovements = useCallback(() => api.get("/pos/movements").then((r) => setMovements(r.data)).catch(() => {}), []);
  const loadSettings = useCallback(() => api.get("/pos/settings").then((r) => setSettings(r.data)).catch(() => {}), []);
  const loadDash = useCallback(() => api.get("/pos/dashboard").then((r) => setDash(r.data)).catch(() => {}), []);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => { if (user && user.role === "visor") navigate("/admin"); }, [user, navigate]);
  useEffect(() => { if (user && user.must_change_password) navigate("/admin/cambiar-clave"); }, [user, navigate]);
  const navItems = NAV.filter((n) => isAdmin || n.cashier);
  const item = (key) => { const n = NAV.find((x) => x.key === key); return { key, label: n.label, icon: n.icon }; };
  const posGroups = isAdmin
    ? [
        { key: "ventas", label: "Ventas", items: [item("facturar"), item("facturas"), item("cotizaciones"), item("cierre")] },
        { key: "catalogo", label: "Catálogo", items: [item("productos"), item("inventario"), item("clientes")] },
        { key: "analisis", label: "Análisis", items: [item("inicio"), item("reportes"), item("cxc"), item("cajachica"), item("auditoria")] },
        { key: "sistema", label: "Sistema", items: [item("usuarios"), item("config")] },
      ]
    : [
        { key: "ventas", label: "Ventas", items: [item("facturar"), item("facturas"), item("cotizaciones"), item("cierre")] },
        { key: "clientes", label: "Clientes", items: [item("clientes")] },
      ];
  useEffect(() => { if (user && !isAdmin) setTab("facturar"); }, [user, isAdmin]);
  useEffect(() => {
    if (!user) return;
    loadProducts(); loadCustomers(); loadInvoices(); loadSettings();
    if (isAdmin) { loadMovements(); loadDash(); }
  }, [user, isAdmin, loadProducts, loadCustomers, loadInvoices, loadMovements, loadSettings, loadDash]);

  useEffect(() => {
    if (tab === "inicio" && isAdmin) loadDash();
    if (tab === "facturas") loadInvoices();
    if (tab === "inventario" && isAdmin) loadMovements();
  }, [tab, isAdmin, loadDash, loadInvoices, loadMovements]);

  const afterInvoice = (inv) => {
    setViewInvoice(inv);
    if (isAdmin) loadDash();
    loadInvoices();
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><span className="font-display text-4xl animate-pulse">MO</span></div>;

  return (
    <div className="min-h-screen bg-neutral-50 text-espresso">
      <TopNav mode="pos" groups={posGroups} active={tab} onSelect={setTab} preferencesKey="config" />

      <main className="p-5 md:p-8 max-w-[1600px] mx-auto">
        {tab === "inicio" && isAdmin && <Inicio dash={dash} onView={setViewInvoice} go={setTab} />}
        {tab === "facturar" && <Facturar products={products} customers={customers} settings={settings} user={user} reload={() => { loadProducts(); }} reloadCustomers={loadCustomers} onInvoice={afterInvoice} prefill={convertQuote} onPrefillDone={() => setConvertQuote(null)} />}
        {tab === "facturas" && <Facturas invoices={invoices} customers={customers} products={products} reload={() => { loadInvoices(); loadProducts(); }} onView={setViewInvoice} settings={settings} isAdmin={isAdmin} />}
        {tab === "cotizaciones" && <Cotizaciones products={products} customers={customers} settings={settings} reloadCustomers={loadCustomers} onConvert={(qz) => { setConvertQuote(qz); setTab("facturar"); }} />}
        {tab === "productos" && isAdmin && <Productos products={products} reload={loadProducts} />}
        {tab === "clientes" && <Clientes customers={customers} reload={loadCustomers} settings={settings} onView={setViewInvoice} isAdmin={isAdmin} />}
        {tab === "inventario" && isAdmin && <Inventario products={products} movements={movements} settings={settings} reload={() => { loadProducts(); loadMovements(); }} />}
        {tab === "reportes" && isAdmin && <Reportes />}
        {tab === "cierre" && <CierreCaja settings={settings} />}
        {tab === "cxc" && isAdmin && <CuentasPorCobrar settings={settings} />}
        {tab === "cajachica" && isAdmin && <CajaChica />}
        {tab === "usuarios" && isAdmin && <Usuarios />}
        {tab === "auditoria" && isAdmin && <Auditoria />}
        {tab === "config" && isAdmin && <Configuracion settings={settings} reloadSettings={loadSettings} />}
      </main>

      {viewInvoice && <InvoicePrint invoice={viewInvoice} settings={settings} customers={customers} onClose={() => setViewInvoice(null)} />}
      <OfflineSync products={products} settings={settings} onSynced={() => { loadInvoices(); loadProducts(); if (isAdmin) loadDash(); }} />
    </div>
  );
}

function Inicio({ dash, onView, go }) {
  if (!dash) return <p className="text-neutral-400">Cargando...</p>;
  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Inicio</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card l="Ventas del día" v={rd(dash.sales_day)} />
        <Card l="Ventas del mes" v={rd(dash.sales_month)} />
        <Card l="Productos disponibles" v={dash.products_available} />
        <Card l="Valor inventario" v={rd(dash.inventory_value)} />
      </div>

      <SalesChart dash={dash} />

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="font-medium mb-3">Facturas recientes</h2>
          <div className="space-y-2">
            {dash.recent_invoices.map((i) => (
              <button key={i.id} onClick={() => onView(i)} className="w-full flex items-center justify-between text-sm border-b border-neutral-50 py-2 hover:bg-neutral-50 px-1 rounded">
                <span>{i.number} · {i.customer_name}</span>
                <span className="font-medium">{rd(i.total)}</span>
              </button>
            ))}
            {dash.recent_invoices.length === 0 && <p className="text-neutral-400 text-sm py-4 text-center">Aún no hay facturas. <button onClick={() => go("facturar")} className="text-rose underline">Crear la primera</button></p>}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="font-medium mb-3">Más vendidos</h2>
          <div className="space-y-2">
            {dash.top_products.length ? dash.top_products.map((p, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-neutral-50 py-1.5"><span className="text-neutral-600">{p.name}</span><span className="font-medium">{p.qty}</span></div>
            )) : <p className="text-neutral-400 text-sm">Sin datos.</p>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Alerts title="Pocas unidades" items={dash.low_stock} color="text-amber-600" />
        <Alerts title="Agotados" items={dash.out_of_stock} color="text-red-500" />
      </div>

      {dash.sample_restock && dash.sample_restock.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-6" data-testid="pos-sample-restock">
          <h2 className="font-medium mb-3 flex items-center gap-2 text-rose"><AlertTriangle size={16} /> Reponer (productos usados como muestra)</h2>
          <div className="space-y-1.5">
            {dash.sample_restock.map((p) => (
              <div key={p.id} className="flex justify-between items-center text-sm border-b border-neutral-50 py-1.5">
                <span className="text-neutral-600">{p.name} <span className="text-xs text-neutral-400">· {p.sample_units} usadas como muestra</span></span>
                <span className={`font-medium ${p.qty <= 0 ? "text-red-500" : "text-amber-600"}`}>stock {p.qty}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Card = ({ l, v }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    <p className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</p>
    <p className="text-2xl font-serif mt-1">{v}</p>
  </div>
);

function SalesChart({ dash }) {
  const [mode, setMode] = useState("day");
  const series = mode === "day" ? (dash.sales_by_day || []) : (dash.sales_by_week || []);
  const empty = series.every((d) => !d.total);
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-6" data-testid="pos-sales-chart">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-medium">Tendencia de ventas</h2>
        <div className="flex bg-neutral-100 rounded-full p-1 text-xs">
          <button data-testid="chart-mode-day" onClick={() => setMode("day")}
            className={`px-3 py-1.5 rounded-full transition ${mode === "day" ? "bg-espresso text-cream" : "text-neutral-500"}`}>Últimos 14 días</button>
          <button data-testid="chart-mode-week" onClick={() => setMode("week")}
            className={`px-3 py-1.5 rounded-full transition ${mode === "week" ? "bg-espresso text-cream" : "text-neutral-500"}`}>Últimas 8 semanas</button>
        </div>
      </div>
      {empty ? (
        <p className="text-neutral-400 text-sm py-16 text-center">Aún no hay ventas para graficar en este período.</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#B76E79" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#B76E79" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9c968c" }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9c968c" }} tickLine={false} axisLine={false}
              tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
            <Tooltip formatter={(v) => rd(v)} labelStyle={{ color: "#2C2621" }}
              contentStyle={{ borderRadius: 10, border: "1px solid #eee", fontSize: 12 }} />
            <Area type="monotone" dataKey="total" name="Ventas" stroke="#B76E79" strokeWidth={2} fill="url(#salesFill)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const Alerts = ({ title, items, color }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-5">
    <h2 className={`font-medium mb-3 flex items-center gap-2 ${color}`}><AlertTriangle size={16} /> {title}</h2>
    <div className="space-y-1.5">
      {items.length ? items.map((p) => (
        <div key={p.id} className="flex justify-between text-sm border-b border-neutral-50 py-1.5"><span className="text-neutral-600">{p.name}</span><span className="font-medium">{p.qty}</span></div>
      )) : <p className="text-neutral-400 text-sm">Todo en orden.</p>}
    </div>
  </div>
);
